import type {
  CurationColumn,
  CurationColumnInput,
  CurationValueType,
} from "@/features/data-curation/types";

/** Types a person can choose. "figure" stays only on old columns that have it. */
export const COLUMN_TYPES: CurationValueType[] = [
  "text",
  "integer",
  "number",
  "boolean",
  "categorical",
  "date",
  "json",
  "image",
];
/** Types of the columns of a Fast dataset: the builder fills no images. */
export const FAST_COLUMN_TYPES: CurationValueType[] = [
  "text",
  "integer",
  "number",
  "boolean",
  "categorical",
];
export const MAX_COLUMNS = 200;
export const MAX_LABEL_LENGTH = 300;
export const MAX_NOTE_LENGTH = 2000;
const MAX_EXAMPLES = 10;
const MAX_OPTIONS = 50;
const MAX_OPTION_LENGTH = 200;

/** One column while a person edits the proposed schema. */
export interface ColumnDraft {
  key: string;
  /** The saved column, or null for a column added in this edit. */
  original: CurationColumn | null;
  label: string;
  valueType: CurationValueType;
  description: string;
  normalization: string;
  /** One example per line. */
  examplesText: string;
  /** One option per line. Only a categorical column uses them. */
  optionsText: string;
  required: boolean;
}

export interface ColumnDraftErrors {
  label?: string;
  examples?: string;
  options?: string;
}

type ParsedExamples =
  | { values: unknown[]; error?: undefined }
  | { values?: undefined; error: string };

const PLAIN_EXAMPLE_TYPES = new Set<CurationValueType>(["text", "categorical", "date"]);

const examplesToText = (examples: unknown[], valueType: CurationValueType) =>
  examples
    .map((example) =>
      typeof example === "string" && PLAIN_EXAMPLE_TYPES.has(valueType)
        ? example
        : JSON.stringify(example),
    )
    .join("\n");

export const toColumnDrafts = (columns: CurationColumn[]): ColumnDraft[] =>
  columns.map((column) => ({
    key: column.column_id,
    original: column,
    label: column.label,
    valueType: column.value_type,
    description: column.description ?? "",
    normalization: column.normalization_rules ?? "",
    examplesText: examplesToText(column.examples ?? [], column.value_type),
    optionsText: (column.options ?? []).join("\n"),
    required: column.required,
  }));

let newColumnCount = 0;

export const newColumnDraft = (): ColumnDraft => ({
  key: `new-column-${++newColumnCount}`,
  original: null,
  label: "",
  valueType: "text",
  description: "",
  normalization: "",
  examplesText: "",
  optionsText: "",
  required: false,
});

const isValidDate = (value: string) => {
  if (!/^\d{4}(-(0[1-9]|1[0-2])(-\d{2})?)?$/.test(value) || value.startsWith("0000")) {
    return false;
  }
  if (value.length < 10) return true;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(value);
};

/** Mirrors the server: an example must already have the column's type. */
const parseExample = (
  line: string,
  valueType: CurationValueType,
): { value: unknown } | null => {
  switch (valueType) {
    case "text":
    case "categorical":
      return { value: line };
    case "integer": {
      const value = Number(line);
      return /^[-+]?\d+$/.test(line) && Number.isSafeInteger(value) ? { value } : null;
    }
    case "number": {
      const value = Number(line);
      return /^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(line) && Number.isFinite(value)
        ? { value }
        : null;
    }
    case "boolean":
      return /^(true|false)$/i.test(line) ? { value: line.toLowerCase() === "true" } : null;
    case "date":
      return isValidDate(line) ? { value: line } : null;
    default:
      try {
        return { value: JSON.parse(line) as unknown };
      } catch {
        return null;
      }
  }
};

const EXAMPLE_ERRORS: Partial<Record<CurationValueType, string>> = {
  integer: "Each example must be a whole number, such as 12.",
  number: "Each example must be a number, such as 0.72.",
  boolean: "Each example must be true or false.",
  date: "Each example must be a date, such as 2024-01-31, 2024-01, or 2024.",
};

const parseExamples = (text: string, valueType: CurationValueType): ParsedExamples => {
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length > MAX_EXAMPLES) {
    return { error: `Use at most ${MAX_EXAMPLES} examples.` };
  }
  const values: unknown[] = [];
  for (const line of lines) {
    const parsed = parseExample(line, valueType);
    if (!parsed) {
      return { error: EXAMPLE_ERRORS[valueType] ?? "Write each example as JSON." };
    }
    values.push(parsed.value);
  }
  return { values };
};

const draftExamples = (draft: ColumnDraft): ParsedExamples => {
  const { original } = draft;
  if (
    original &&
    draft.valueType === original.value_type &&
    draft.examplesText === examplesToText(original.examples ?? [], original.value_type)
  ) {
    return { values: original.examples ?? [] };
  }
  return parseExamples(draft.examplesText, draft.valueType);
};

type ParsedOptions =
  | { values: string[]; error?: undefined }
  | { values?: undefined; error: string };

/** Mirrors the server: only a categorical column has options, and each is different. */
const draftOptions = (draft: ColumnDraft): ParsedOptions => {
  if (draft.valueType !== "categorical") return { values: [] };
  const values = draft.optionsText
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  if (values.length > MAX_OPTIONS) return { error: `Use at most ${MAX_OPTIONS} options.` };
  if (values.some((value) => value.length > MAX_OPTION_LENGTH)) {
    return { error: `Each option must have at most ${MAX_OPTION_LENGTH} characters.` };
  }
  if (new Set(values.map((value) => value.toLowerCase())).size !== values.length) {
    return { error: "Each option must be different." };
  }
  return { values };
};

const note = (value: string | null | undefined) => value?.trim() || null;

const sameExamples = (examples: unknown[], original: CurationColumn) =>
  JSON.stringify(examples) === JSON.stringify(original.examples ?? []);

const sameOptions = (options: string[], original: CurationColumn) =>
  JSON.stringify(options) === JSON.stringify(original.options ?? []);

/** The server keeps type, notes, examples, options, and required fixed for a column_id. */
const definitionChanged = (draft: ColumnDraft, examples: unknown[], options: string[]) => {
  const { original } = draft;
  return (
    !original ||
    draft.valueType !== original.value_type ||
    draft.required !== original.required ||
    note(draft.description) !== note(original.description) ||
    note(draft.normalization) !== note(original.normalization_rules) ||
    !sameExamples(examples, original) ||
    !sameOptions(options, original)
  );
};

/** Same identity as the server's normalize_curation_label. */
const labelIdentity = (label: string) =>
  label.normalize("NFKC").split(/\s+/).filter(Boolean).join(" ").toLowerCase();

export const validateColumnDrafts = (drafts: ColumnDraft[]) => {
  const errors: Record<string, ColumnDraftErrors> = {};
  const seen = new Set<string>();
  let problems = 0;
  for (const draft of drafts) {
    const draftErrors: ColumnDraftErrors = {};
    const identity = labelIdentity(draft.label);
    if (!identity) {
      draftErrors.label = "Enter a label.";
    } else if (seen.has(identity)) {
      draftErrors.label = "Another column has this label.";
    }
    seen.add(identity);
    const examples = draftExamples(draft);
    if (examples.error) draftErrors.examples = examples.error;
    const options = draftOptions(draft);
    if (options.error !== undefined) {
      draftErrors.options = options.error;
    } else if (
      options.values.length > 0 &&
      examples.values?.some((example) => !options.values.includes(example as string))
    ) {
      draftErrors.examples = "Each example must be one of the options.";
    }
    if (draftErrors.label || draftErrors.examples || draftErrors.options) {
      errors[draft.key] = draftErrors;
      problems +=
        Number(Boolean(draftErrors.label)) +
        Number(Boolean(draftErrors.examples)) +
        Number(Boolean(draftErrors.options));
    }
  }
  const general =
    drafts.length === 0
      ? "Keep at least one column."
      : drafts.length > MAX_COLUMNS
        ? `Use at most ${MAX_COLUMNS} columns.`
        : null;
  return { errors, general, problems: problems + Number(Boolean(general)) };
};

/**
 * Body for the schema replace. A column whose definition changed goes without
 * its column_id, so the server stores it as a new column. So does a column that
 * is gone from the latest schema: the server would give an unknown column_id
 * default fields.
 */
export const columnsPayload = (
  drafts: ColumnDraft[],
  latestColumns: CurationColumn[],
): CurationColumnInput[] => {
  const latestIds = new Set(latestColumns.map((column) => column.column_id));
  return drafts.map((draft) => {
    const examples = draftExamples(draft).values ?? [];
    const options = draftOptions(draft).values ?? [];
    const label = draft.label.trim();
    if (
      draft.original &&
      latestIds.has(draft.original.column_id) &&
      !definitionChanged(draft, examples, options)
    ) {
      return { column_id: draft.original.column_id, label };
    }
    return {
      label,
      value_type: draft.valueType,
      description: note(draft.description),
      normalization_rules: note(draft.normalization),
      examples,
      ...(options.length > 0 ? { options } : {}),
      required: draft.required,
    };
  });
};

export const hasColumnChanges = (drafts: ColumnDraft[], columns: CurationColumn[]) =>
  drafts.length !== columns.length ||
  drafts.some((draft, index) => {
    if (!draft.original || draft.original.column_id !== columns[index]?.column_id) return true;
    if (draft.label.trim() !== draft.original.label) return true;
    const examples = draftExamples(draft);
    const options = draftOptions(draft);
    return (
      examples.error !== undefined ||
      options.error !== undefined ||
      definitionChanged(draft, examples.values, options.values)
    );
  });

/** Short lines that tell the agent what the person changed. */
export const describeColumnChanges = (
  drafts: ColumnDraft[],
  columns: CurationColumn[],
): string[] => {
  const kept = new Set(drafts.map((draft) => draft.original?.column_id));
  const added = drafts.filter((draft) => !draft.original).map((draft) => draft.label.trim());
  const removed = columns
    .filter((column) => !kept.has(column.column_id))
    .map((column) => column.label);
  const renamed: string[] = [];
  const changed: string[] = [];
  for (const draft of drafts) {
    const { original } = draft;
    if (!original) continue;
    const label = draft.label.trim();
    if (label !== original.label) renamed.push(`${original.label} to ${label}`);
    const examples = draftExamples(draft).values ?? original.examples ?? [];
    const options = draftOptions(draft).values ?? original.options ?? [];
    const parts = [
      draft.valueType !== original.value_type &&
        `type ${original.value_type} to ${draft.valueType}`,
      draft.required !== original.required && (draft.required ? "now required" : "now optional"),
      note(draft.description) !== note(original.description) && "description",
      note(draft.normalization) !== note(original.normalization_rules) && "normalization rules",
      !sameExamples(examples, original) && "examples",
      !sameOptions(options, original) && "options",
    ].filter((part): part is string => typeof part === "string");
    if (parts.length) changed.push(`${label} (${parts.join(", ")})`);
  }
  const lines: string[] = [];
  if (added.length) lines.push(`Added: ${added.join("; ")}`);
  if (removed.length) lines.push(`Removed: ${removed.join("; ")}`);
  if (renamed.length) lines.push(`Renamed: ${renamed.join("; ")}`);
  if (changed.length) lines.push(`Changed: ${changed.join("; ")}`);
  return lines;
};

/**
 * A change to a saved column that can leave cells that do not fit it: a new type
 * or new options, or a column that is now required. The server then checks the cells.
 */
export const changeChecksCells = (draft: ColumnDraft) => {
  const { original } = draft;
  if (!original) return false;
  const options = draftOptions(draft).values;
  return (
    draft.valueType !== original.value_type ||
    (options !== undefined && !sameOptions(options, original)) ||
    (draft.required && !original.required)
  );
};

/** The chat message that asks the agent to fix the cells that a column change left wrong. */
export const fixColumnMessage = (datasetId: string, label: string) =>
  [
    `I changed the column "${label}" on the Datasets page. My note above lists the cells that do not fit it now.`,
    "",
    `Fix those cells from their sources. First read the columns with \`paperclip extraction get ${datasetId}\`. Do not write the other cells again.`,
  ].join("\n");

/** The chat message that asks the agent to fill a column the person added on the page. */
export const addColumnMessage = (datasetId: string, label: string) =>
  [
    `I added the column "${label}" to the dataset on the Datasets page. It is optional, and its cells are empty.`,
    "",
    `Fill it for the rows that are in the grid, as "Add a column after approval" in the routine tells. First read the columns with \`paperclip extraction get ${datasetId}\`. Do not add the column again, and do not write the other cells again.`,
  ].join("\n");

/** The chat message that tells the agent the person approved the schema. */
export const approvalMessage = (datasetId: string, changes: string[]) => {
  const lines = ["I approved the schema on the Datasets page, so the dataset is now active."];
  if (changes.length) {
    lines.push("", "Before I approved it, I changed the columns:");
    lines.push(...changes.map((change) => `- ${change}`));
  }
  lines.push(
    "",
    `Read the approved columns with \`paperclip extraction get ${datasetId}\` before you continue${
      changes.length ? ", because they are not the same as your proposal." : "."
    }`,
    "Do not run `paperclip extraction start` again. Save the collection plan with `paperclip extraction plan update` and continue the collection.",
  );
  return lines.join("\n");
};

/** The chat message that starts the collection of columns that the person approved earlier. */
export const startCollectionMessage = (datasetId: string) =>
  [
    "I started the collection on the Datasets page. I approved the columns before.",
    "",
    `Read the columns with \`paperclip extraction get ${datasetId}\` and the plan with \`paperclip extraction plan show ${datasetId}\` before you continue.`,
    "Do not run `paperclip extraction start` again. Save the collection plan with `paperclip extraction plan update` and continue the collection.",
  ].join("\n");

/**
 * The message that the person typed in the chat box but did not send goes
 * first, so the agent uses it before it collects rows.
 */
export const withUnsentMessage = (unsent: string, message: string) =>
  unsent
    ? [unsent, "", "Use what I wrote above for the plan before you collect rows.", "", message].join("\n")
    : message;
